import {CommonInput} from '@/components/common-components';
import {Button, Checkbox, GrayButton} from '@/components/common-components/v2';
import {useStore} from '@/store/root-store';
import {LoadingOutlined} from '@ant-design/icons';
import {faCaretDown, faMagnifyingGlass} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Popover, Spin, Tooltip} from 'antd';
import {toJS} from 'mobx';
import {observer} from 'mobx-react';
import {useEffect, useState} from 'react';
import styled from 'styled-components';

export const TermsSelectionDropdown = observer(() => {
  const [selectedTerms, setSelectedTerms] = useState<any[]>([]);
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showLimitError, setShowLimitEror] = useState<boolean>(false);


  const {
    contentOptimizer: {
      currentPage: {
        setIsTaActive,
        setFocusTermsForInsertion,
        fetchTaSuggestions,
        termsForInsertion,
        analytics,
        taSuggestionsLoading,
      },
    }, settings: {
      customer: {getCustomerQuota},
    }} = useStore('');
  const {settings: {customer: {profile: {quotaUtilization}}}} = useStore('');

  const antIcon = <LoadingOutlined style={{fontSize: '10px !important', color: '#7f4ead', marginRight: 10}} spin />;

  useEffect(() => {
    setSelectedTerms([...termsForInsertion ?? []]);
  }, [termsForInsertion]);

  const handleCheckboxClick = (e: any, term: any) => {
    let updatedList = selectedTerms;


    if (e?.target?.checked) {
      if (updatedList?.length > 49) {
        setShowLimitEror(true);
      } else {
        setShowLimitEror(false);
        updatedList = [
          ...updatedList,
          term,
        ];
      }
    } else {
      updatedList = updatedList?.filter(item => item.name !== term.name);
      if (updatedList?.length < 51) {
        setShowLimitEror(false);
      }
    }


    setSelectedTerms(updatedList);
  };
  const onTermsSearchHandler = (query: string) => {
    setSearchQuery(query ?? '');
  };

  const onSubmitHandler = async () => {
    setFocusTermsForInsertion([...selectedTerms].map(term => toJS(term)));

    try {
      await fetchTaSuggestions(selectedTerms.map(term => term.name));
      getCustomerQuota();
      setIsTaActive(true);
    } catch (e) {
      return Promise.reject(e);
    } finally {
      setPopoverVisible(false);
    }
  };

  const onBulkSelectTop20Handler = () => {
    const addCompleteTerms = (completeTermsLength: number) => {
      const includedTerms = toJS(analytics?.includedTerms);

      prepcCompleteTerms = includedTerms?.sort((a, b) => a.normalizedScore > b.normalizedScore).slice(0, completeTermsLength);
    };

    const includeMoreTerms = toJS(analytics?.includeMoreTerms);

    if (!includeMoreTerms?.length) {
      if (!analytics?.includedTerms?.length) {
        return false;
      }
      addCompleteTerms(20);
    }


    let prepIncludeMoreTerms = [];
    let prepcCompleteTerms = [];

    prepIncludeMoreTerms = includeMoreTerms?.sort((a, b) => a.normalizedScore > b.normalizedScore).slice(0, 20);

    if (includeMoreTerms.length < 20) {
      addCompleteTerms(20 - includeMoreTerms.length);
    }

    const finalPrepArr = [...prepIncludeMoreTerms, ...prepcCompleteTerms];

    setSelectedTerms(finalPrepArr);
  };

  const onUncheckAllClickHandler = () => {
    setSelectedTerms([]);
  };

  const onTriggerClickHandler = () => {
    setPopoverVisible(!popoverVisible);
    setSelectedTerms([...termsForInsertion ?? []]);
  };

  const popoverContent = (
    <Wrapper>
      <HeadingSection>
        <SearchBox>
          <FontAwesomeIcon icon={faMagnifyingGlass} color='#a3a4a4' style={{marginBottom: 2}}/>
          <CommonInputStyled
            placeholder='Search terms'
            onChange={e => onTermsSearchHandler(e.target.value)}/>
        </SearchBox>
        <Tooltip placement='bottomLeft' title='Updating terms costs 1 point' overlayInnerStyle={{backgroundColor: '#000'}}>
          <QuotaInfo>
            {`AI Quota: ${quotaUtilization?.ca?.allowedAiContentGeneration?.consumed}/${quotaUtilization?.ca?.allowedAiContentGeneration?.total}`}
          </QuotaInfo>
        </Tooltip>
      </HeadingSection>

      <ControllsBar>
        <BulkControllBtn onClick={() => onBulkSelectTop20Handler()}>Select top 20 terms</BulkControllBtn>
        <BulkControllBtn onClick={() => onUncheckAllClickHandler()}>Uncheck all</BulkControllBtn>
      </ControllsBar>
      {showLimitError ?
        <div style={{fontSize: 12, color: '#f44343', marginTop: 5}}>Please select less than 50 topical terms.</div> :
        null}
      <TermsList>
        {[
          ...(analytics?.includeMoreTerms ?? []),
          ...(analytics?.includedTerms ?? []),
          ...(analytics?.includeLessTerms ?? []),
        ].filter(term => term.name.includes(searchQuery))?.map((term, idx) => {
          return <SingleTermRow key={idx} opaque={term.status == 'include-less'}>
            <CheckboxStyled
              checked={selectedTerms?.map(item => item.name).includes(term.name)}
              onChange={e => handleCheckboxClick(e, term)}
              disabled={term.status == 'include-less'}>
              <TermName>{term.name}</TermName>
            </CheckboxStyled>
            <Tooltip
              overlayInnerStyle={{backgroundColor: '#000', width: 220}}
              placement={'bottomLeft'}
              title={<div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <span>Current usage:</span>
                  <span>{term.usedCount}</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <span>Recommended usage:</span>
                  <span>{term.requiredCountMin}</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <span>Maximum usage:</span>
                  <span>{term.requiredCountMax}</span>
                </div>
              </div>}>
              <TermUsage>{`${term.usedCount}/${term.requiredCountMin}`}</TermUsage>
            </Tooltip>
          </SingleTermRow>;
        })}
      </TermsList>
      <BtnsContainer>
        <GrayButton disabled={taSuggestionsLoading} onClick={() => setPopoverVisible(false)}>Cancel</GrayButton>
        <Button disabled={taSuggestionsLoading} color='blue' onClick={() => onSubmitHandler()} style={{marginRight: 0}}>
          {taSuggestionsLoading ? <Spin indicator={antIcon}/> : null}
          {taSuggestionsLoading ? 'Updating terms...' : 'Update terms'}
        </Button>
      </BtnsContainer>
    </Wrapper>
  );


  return <Popover
    trigger='click'
    visible={popoverVisible}
    content={popoverContent}
    placement='bottomLeft'
    overlayInnerStyle={{
      borderRadius: 5,
      background: '#fff',
      border: '1px solid #e8e8e8',
      boxShadow: '0px 6px 25px rgba(0, 0, 0, 0.15)',
    }}>
    <Trigger onClick={() => onTriggerClickHandler()}>
      {`Select terms ${selectedTerms?.length}`}
      <FontAwesomeIcon icon={faCaretDown} style={{marginLeft: 5}}/>
    </Trigger>
  </Popover>;
});
const Trigger = styled.div`
  color: #34aef3 !important;
  font-size: 12px;
  cursor: pointer;
`;
const Wrapper = styled.div`

`;
const HeadingSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const SearchBox = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;
const QuotaInfo = styled.div`
  color: #4E5156;
  margin-bottom: 12px;
`;
const CommonInputStyled = styled(CommonInput)`
  border: 0px solid transparent;
  height: 30px;

  &:active {
    border: 0px solid transparent;
    box-shadow: 0px 0px 0px 0px transparent;
  }
  &:hover {
    border: 0px solid transparent;
    box-shadow: 0px 0px 0px 0px transparent;
  }

  &::placeholder {
    color: #A3A4A4;
    font-weight: 400;
    font-size: 15px;
  }
`;
const ControllsBar = styled.div`
  display: flex;
  justify-content: space-between;
  color: #2D6CCA;
  font-size: 12px;
  background: #F9F9FB;
  box-shadow: 0px 1px 0px #E8E8E8, 0px -1px 0px #E8E8E8;
  margin-left: -15px;
  margin-right: -15px;
  padding: 10px 15px
`;
const BulkControllBtn = styled.div`
  cursor: pointer;
`;

const TermsList = styled.div`
  max-height: 300px;
  overflow-y: scroll;
  margin-right: -15px;
  padding-right: 15px;
  margin-top: 10px;


  // For Internet Explorer
  scrollbar-face-color: rgba(138,138,138,0.12) ;
  scrollbar-track-color: rgba(138,138,138,0.32);

  // For Google Chrome
  &::-webkit-scrollbar {
    height: 5px;
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(138,138,138,0.4);
  }

  &::-webkit-scrollbar-track {
    background: #E8E8E8;
  }
`;
const SingleTermRow = styled.div<{opaque: boolean}>`
  display: flex;

  opacity: ${p => p.opaque ? '.4' : 1};
`;

const CheckboxStyled = styled(Checkbox)`
  display: flex;
  width: 100%;

  .ant-checkbox-wrapper {
    width: 100%;
  }

  .ant-checkbox-inner {
    border-radius: 4px !important;
  }
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #2D6CCA !important;
    border-color: #2D6CCA !important;
    border-radius: 4px !important;
  }
`;

const TermName = styled.div`
  cursor: default;
  margin-left: 10px;
  color: #4E5156;
`;
const TermUsage = styled.div`
  cursor: default;
  margin-left: auto;
  color: #4E5156;
`;
const BtnsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
`;
